import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Col, Row, Nav, Card, Button } from "react-bootstrap";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { RecurringPaymentsTable } from "../components/tables/recurringPaymentTable";
import TransactionsSidebar from "../components/common/transactionsSidebar";
import CostsEstimates from "./costsEstimates";
import ProviderAndClaimsTable from "../components/tables/providersTable";
import useManageVisits from "../hooks/patientDashboard/useManageVisits";
import useManageBillAndInsurance from "../hooks/useManageBillAndInsurance";
import PaymentHistoryTable from "../components/tables/PaymentHistoryTable";
import useProfileStore from "../store/patient/profileStore";
import useManageBills from "../hooks/patientDashboard/useManageBills";

export default function Invoice() {
  const [tabs, setTabs] = useState("invoices");
  const location = useLocation();
  const navigate = useNavigate();
  const { handleFetchVisitsSummary, loading } = useManageVisits();
  const { loading: claimsLoading, claims, handleInsuranceClaims } = useManageBillAndInsurance();
  const { loading: loadingPayments, payments, handlePaymentHistory } = useManageBillAndInsurance();
  const {
    userBills,
    loading: loadingBills,
    findPatientBills,
    findPatientPendingBills,
    findPatientBillsAlt,
    recurring,
    generatePayment,
    paymentData,
  } = useManageBills();
  const { profile } = useProfileStore();

  useEffect(() => {
    handleFetchVisitsSummary();
    handleInsuranceClaims();
    handlePaymentHistory();
  }, []);

  useEffect(() => {
    findPatientPendingBills(profile?.id);
  }, []);

  useEffect(() => {
    if (location.hash) {
      setTabs(location.hash.replace("#", ""));
      return;
    }

    setTabs("invoices");
  }, [location]);

  const handleTabChange = (tab) => {
    setTabs(tab);
    navigate(`#${tab}`);
  };

  const pendingCount = Array.isArray(recurring?.result)
    ? recurring.result.filter((item) => item?.status !== 1).length
    : 0;
  const claimsCount = Array.isArray(claims?.result) ? claims.result.length : 0;
  const paymentCount = Array.isArray(payments) ? payments.length : 0;

  return (
    <React.Fragment>
      <Header />
      <div className="main p-4 p-lg-5 mt-5">
        <div className="data-page-hero">
          <div className="data-page-copy">
            <span className="data-page-kicker">Billing & Insurance</span>
            <h2>Stay on top of bills, claims, and payments</h2>
            <p>
              Move through pending invoices, insurance activity, and payment records from one calmer
              billing workspace.
            </p>
          </div>
          <div className="data-page-stats">
            <div className="data-page-stat">
              <small>Pending Bills</small>
              <strong>{pendingCount}</strong>
            </div>
            <div className="data-page-stat">
              <small>Claims</small>
              <strong>{claimsCount}</strong>
            </div>
            <div className="data-page-stat">
              <small>Payments</small>
              <strong>{paymentCount}</strong>
            </div>
          </div>
        </div>

        <Row className="g-5">
          <Col xl>
            <Nav className="nav-line d-none d-lg-flex flex-wrap mb-0 gap-0">
              <Button onClick={() => handleTabChange("invoices")} variant="link" className={tabs === "invoices" ? "active" : ""}>
                Invoice
              </Button>
              <Button onClick={() => handleTabChange("claims")} variant="link" className={tabs === "claims" ? "active" : ""}>
                Claims
              </Button>
              <Button
                onClick={() => handleTabChange("payment-history")}
                variant="link"
                className={tabs === "payment-history" ? "active" : ""}
              >
                Payment History
              </Button>
            </Nav>

            <Row className="g-3">
              {tabs === "invoices" && (
                <Card className="data-panel">
                  <RecurringPaymentsTable
                    userBills={userBills}
                    loading={loadingBills}
                    generatePayment={generatePayment}
                    paymentData={paymentData}
                    findPatientBills={findPatientBills}
                    findPatientPendingBills={findPatientPendingBills}
                    findPatientBillsAlt={findPatientBillsAlt}
                    recurring={recurring}
                  />
                </Card>
              )}
              {tabs === "claims" && (
                <Card className="data-panel">
                  <ProviderAndClaimsTable data={claims} loading={claimsLoading || loading} />
                </Card>
              )}
              {tabs === "payment-history" && (
                <Card className="data-panel">
                  <PaymentHistoryTable data={payments} loading={loadingPayments} />
                </Card>
              )}
              {tabs === "estimates" && <CostsEstimates />}
            </Row>
          </Col>

          <Col xl="4" className="d-none d-xl-block">
            <TransactionsSidebar />
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
